import React, { useState, useEffect } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const PharmacyDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // --- DATA ---

  // 1. Sales vs Purchase Data
  const salesVsPurchaseData = [
    { name: "Mon", Sales: 12000, Purchase: 8000 },
    { name: "Tue", Sales: 15000, Purchase: 10000 },
    { name: "Wed", Sales: 18000, Purchase: 12000 },
    { name: "Thu", Sales: 14000, Purchase: 9000 },
    { name: "Fri", Sales: 22000, Purchase: 15000 },
    { name: "Sat", Sales: 25000, Purchase: 18000 },
    { name: "Sun", Sales: 19000, Purchase: 11000 },
  ];

  // 2. Pie Chart Data (Category Distribution) - RESTORED
  const categoryData = [
    { name: "Tablets", value: 45, color: "#0088cc" },
    { name: "Syrups", value: 25, color: "#00d4aa" },
    { name: "Injections", value: 15, color: "#f39c12" },
    { name: "Surgicals", value: 15, color: "#667eea" },
  ];

  // 3. Recent Sales Data
  const recentSales = [
    { id: "INV-001", patient: "Ravi Kumar", items: "Paracetamol...", amount: "₹ 450", status: "Paid", type: "success" },
    { id: "INV-002", patient: "Anita Verma", items: "Cough Syrup", amount: "₹ 120", status: "Paid", type: "success" },
    { id: "INV-003", patient: "Suresh Patel", items: "Insulin Pen", amount: "₹ 850", status: "Pending", type: "warning" },
    { id: "INV-004", patient: "Walk-in", items: "Bandages", amount: "₹ 200", status: "Paid", type: "success" },
  ];

  // 4. Top Selling Items
  const topSellingData = [
    { name: "Dolo 650", sales: 120 },
    { name: "Azithral 500", sales: 95 },
    { name: "Pantop-D", sales: 85 },
    { name: "Vit-C Limcee", sales: 70 },
  ];

  // 5. Critical Alerts
  const stockAlerts = [
    { id: 1, name: "Dolo 650mg", status: "Low Stock (10 strips)", type: "danger", icon: "bi-graph-down-arrow" },
    { id: 2, name: "Azithromycin", status: "Expiring in 5 days", type: "warning", icon: "bi-calendar-x" },
  ];

  const formatTime = () => currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  const formatDate = () => currentTime.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1 className="welcome-title">Pharmacy Overview</h1>
          <p className="welcome-subtitle">Inventory, Sales & Purchase Management</p>
        </div>
        <div className="datetime-section">
          <div className="current-time">{formatTime()}</div>
          <div className="current-date">{formatDate()}</div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card card-blue">
          <div className="card-icon"><i className="bi bi-currency-rupee"></i></div>
          <div className="card-content">
            <div className="card-label">Total Sales Today</div>
            <div className="card-value">₹ 24,500</div>
            <div className="card-trend positive"><i className="bi bi-arrow-up"></i> +15% vs yesterday</div>
          </div>
        </div>
        <div className="summary-card card-green">
          <div className="card-icon"><i className="bi bi-capsule"></i></div>
          <div className="card-content">
            <div className="card-label">Medicines In Stock</div>
            <div className="card-value">12,450</div>
            <div className="card-trend positive"><i className="bi bi-check-circle"></i> Healthy Inventory</div>
          </div>
        </div>
        <div className="summary-card card-orange">
          <div className="card-icon"><i className="bi bi-exclamation-triangle"></i></div>
          <div className="card-content">
            <div className="card-label">Low Stock Items</div>
            <div className="card-value">18</div>
            <div className="card-trend negative"><i className="bi bi-arrow-down"></i> Needs Re-order</div>
          </div>
        </div>
        <div className="summary-card card-purple">
          <div className="card-icon"><i className="bi bi-hourglass-split"></i></div>
          <div className="card-content">
            <div className="card-label">Expiring Soon</div>
            <div className="card-value">8</div>
            <div className="card-trend negative"><i className="bi bi-info-circle"></i> Within 30 days</div>
          </div>
        </div>
      </div>

      {/* Main Charts Grid */}
      <div className="charts-grid">
        {/* Sales Trend */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title"><i className="bi bi-graph-up-arrow"></i> Sales vs Purchase</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={salesVsPurchaseData}>
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0088cc" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#0088cc" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e6ed" />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
              <Area type="monotone" dataKey="Sales" stroke="#0088cc" fill="url(#colorSales)" />
              <Area type="monotone" dataKey="Purchase" stroke="#00d4aa" fill="#00d4aa" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart (RESTORED) */}
        <div className="chart-card">
          <div className="chart-header">
            <div className="chart-title"><i className="bi bi-pie-chart-fill"></i> Category Distribution</div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={5}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className="bottom-grid-3-col">
        
        {/* 1. Recent Sales */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title"><i className="bi bi-receipt"></i> Recent Billings</div>
            <button className="view-all-btn">View All</button>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Inv ID</th><th>Items</th><th>Amt</th><th>Status</th></tr>
            </thead>
            <tbody>
              {recentSales.map((sale) => (
                <tr key={sale.id}>
                  <td><span className="fw-bold text-primary">{sale.id}</span></td>
                  <td>{sale.items}</td>
                  <td className="fw-bold">{sale.amount}</td>
                  <td><span className={`status-badge ${sale.type}`}>{sale.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 2. Top Selling (Moved here) */}
        <div className="table-card">
          <div className="table-header">
            <div className="table-title"><i className="bi bi-trophy-fill"></i> Best Sellers</div>
          </div>
          <div className="po-list">
             {topSellingData.map((item, idx) => (
               <div key={idx} className="po-item">
                 <div className="po-icon" style={{background: idx === 0 ? '#fff3cd' : '#e3f2fd', color: idx === 0 ? '#f39c12' : '#0088cc'}}>
                    {idx === 0 ? <i className="bi bi-star-fill"></i> : <i className="bi bi-capsule"></i>}
                 </div>
                 <div className="po-details">
                   <div className="po-supplier">{item.name}</div>
                   <div className="po-id">{item.sales} units sold this week</div>
                 </div>
                 <span className="fw-bold text-dark">#{idx + 1}</span>
               </div>
             ))}
          </div>
        </div>

        {/* 3. Alerts & UPDATED BUTTONS */}
        <div className="side-cards">
          {/* Alerts */}
          <div className="small-card">
            <div className="small-card-header text-danger"><i className="bi bi-bell-fill"></i> Alerts</div>
            <div className="notifications-body">
              {stockAlerts.map((alert) => (
                <div key={alert.id} className={`notification-item ${alert.type}`}>
                  <div className="notif-icon"><i className={`bi ${alert.icon}`}></i></div>
                  <div className="notif-content">
                    <p style={{fontWeight: '600', marginBottom:'2px'}}>{alert.name}</p>
                    <span>{alert.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* ✅ FIXED BUTTONS */}
          <div className="quick-actions-card">
             <div className="small-card-header"><i className="bi bi-lightning-charge-fill"></i> Quick Actions</div>
             <div className="action-buttons-container">
                 
                 <button className="custom-action-btn btn-new-bill">
                    <div className="btn-icon"><i className="bi bi-receipt"></i></div>
                    <div className="btn-text">
                        <span>New Bill</span>
                        <small>Create Invoice</small>
                    </div>
                    <i className="bi bi-chevron-right ms-auto"></i>
                 </button>

                 <button className="custom-action-btn btn-add-stock">
                    <div className="btn-icon"><i className="bi bi-box-seam"></i></div>
                    <div className="btn-text">
                        <span>Add Stock</span>
                        <small>Update Inventory</small>
                    </div>
                    <i className="bi bi-chevron-right ms-auto"></i>
                 </button>

                 <button className="custom-action-btn btn-return">
                    <div className="btn-icon"><i className="bi bi-arrow-counterclockwise"></i></div>
                    <div className="btn-text">
                        <span>Return / Exp</span>
                        <small>Manage Returns</small>
                    </div>
                    <i className="bi bi-chevron-right ms-auto"></i>
                 </button>

             </div>
          </div>
        </div>

      </div>

      <style jsx>{`
        .dashboard-container { padding: 0; }
        
        /* Header & Summary Cards (Same as before) */
        .dashboard-header {
          background: white; border-radius: 16px; padding: 25px; margin-bottom: 25px;
          box-shadow: 0 2px 15px rgba(0, 136, 204, 0.08); display: flex; justify-content: space-between; align-items: center; border: 1px solid #e0e6ed;
        }
        .welcome-title { margin: 0; font-size: 26px; font-weight: 700; color: #2c3e50; }
        .welcome-subtitle { margin: 0; font-size: 14px; color: #7f8c8d; }
        .current-time { font-size: 22px; font-weight: 700; color: #0088cc; text-align: right; }
        .current-date { font-size: 13px; color: #7f8c8d; text-align: right; }

        .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; margin-bottom: 25px; }
        .summary-card { background: white; border-radius: 16px; padding: 20px; display: flex; gap: 15px; align-items: center; box-shadow: 0 2px 15px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; }
        .card-icon { width: 60px; height: 60px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 28px; color: white; flex-shrink: 0; }
        .card-blue .card-icon { background: linear-gradient(135deg, #0088cc, #006ba3); }
        .card-green .card-icon { background: linear-gradient(135deg, #00d4aa, #00a88f); }
        .card-orange .card-icon { background: linear-gradient(135deg, #f39c12, #e67e22); }
        .card-purple .card-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
        .card-content { flex: 1; }
        .card-label { font-size: 12px; color: #7f8c8d; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .card-value { font-size: 28px; font-weight: 700; color: #2c3e50; line-height: 1.2; }
        .card-trend { font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 4px; }
        .card-trend.positive { color: #27ae60; } .card-trend.negative { color: #e74c3c; }

        /* Charts */
        .charts-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 20px; margin-bottom: 25px; }
        .chart-card { background: white; border-radius: 16px; padding: 20px; box-shadow: 0 2px 15px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; }
        .chart-header { display: flex; justify-content: space-between; margin-bottom: 15px; }
        .chart-title { font-size: 16px; font-weight: 700; color: #2c3e50; display: flex; gap: 10px; align-items: center; }
        .chart-title i { color: #0088cc; font-size: 18px; }

        /* Bottom Grid */
        .bottom-grid-3-col { display: grid; grid-template-columns: 1.5fr 1.2fr 1fr; gap: 20px; }
        .table-card { background: white; border-radius: 16px; padding: 20px; box-shadow: 0 2px 15px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; }
        .table-header { display: flex; justify-content: space-between; margin-bottom: 15px; }
        .table-title { font-size: 15px; font-weight: 700; color: #2c3e50; display: flex; gap: 8px; align-items: center; }
        .view-all-btn { padding: 4px 12px; border: 1px solid #0088cc; background: white; color: #0088cc; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; }
        
        .data-table { width: 100%; font-size: 13px; border-collapse: collapse; }
        .data-table th { text-align: left; padding: 10px; background: #f8f9fa; color: #7f8c8d; font-weight: 600; border-bottom: 2px solid #e0e6ed; }
        .data-table td { padding: 12px 10px; border-bottom: 1px solid #f0f4f8; color: #2c3e50; }
        
        .status-badge { padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .status-badge.success { background: #e8f5e9; color: #2e7d32; }
        .status-badge.warning { background: #fff3e0; color: #ef6c00; }
        .status-badge.info { background: #e3f2fd; color: #0277bd; }
        .status-badge.danger { background: #ffebee; color: #c62828; }

        /* List Items */
        .po-list { display: flex; flex-direction: column; gap: 12px; }
        .po-item { display: flex; align-items: center; gap: 12px; padding: 10px; border: 1px solid #f0f4f8; border-radius: 10px; }
        .po-icon { width: 32px; height: 32px; background: #e3f2fd; color: #0088cc; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
        .po-details { flex: 1; }
        .po-supplier { font-size: 13px; font-weight: 600; color: #2c3e50; }
        .po-id { font-size: 11px; color: #7f8c8d; }

        /* Alerts & Actions */
        .side-cards { display: flex; flex-direction: column; gap: 20px; }
        .small-card, .quick-actions-card { background: white; border-radius: 16px; padding: 15px; box-shadow: 0 2px 15px rgba(0,0,0,0.05); border: 1px solid #e0e6ed; }
        .small-card-header { font-size: 14px; font-weight: 700; margin-bottom: 15px; display: flex; gap: 8px; align-items: center; }
        
        .notification-item { display: flex; gap: 10px; padding: 10px; border-radius: 8px; margin-bottom: 8px; }
        .notification-item.danger { background: #ffebee; }
        .notification-item.warning { background: #fff3e0; }
        .notif-icon { width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
        .notification-item.danger .notif-icon { background: white; color: #c62828; }
        .notification-item.warning .notif-icon { background: white; color: #ef6c00; }
        .notif-content p { margin: 0; font-size: 12px; font-weight: 600; color: #2c3e50; }
        .notif-content span { font-size: 10px; color: #7f8c8d; }

        /* ✅ NEW BUTTON STYLES */
        .action-buttons-container { display: flex; flex-direction: column; gap: 10px; }
        
        .custom-action-btn {
           display: flex; align-items: center; gap: 12px;
           width: 100%; padding: 12px 15px; border: none; border-radius: 12px;
           background: #f8f9fa; transition: all 0.3s ease; cursor: pointer;
           text-align: left;
        }
        
        .custom-action-btn:hover { transform: translateY(-3px); box-shadow: 0 5px 15px rgba(0,0,0,0.05); }

        .btn-icon {
           width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 18px; color: white;
        }

        .btn-text { display: flex; flex-direction: column; line-height: 1.2; }
        .btn-text span { font-size: 14px; font-weight: 700; color: #2c3e50; }
        .btn-text small { font-size: 11px; color: #7f8c8d; font-weight: 500; }

        /* Button Colors */
        .btn-new-bill .btn-icon { background: linear-gradient(135deg, #0088cc, #006ba3); }
        .btn-new-bill:hover .btn-icon { box-shadow: 0 4px 10px rgba(0, 136, 204, 0.3); }

        .btn-add-stock .btn-icon { background: linear-gradient(135deg, #00d4aa, #00a88f); }
        .btn-add-stock:hover .btn-icon { box-shadow: 0 4px 10px rgba(0, 212, 170, 0.3); }

        .btn-return .btn-icon { background: linear-gradient(135deg, #ff6b6b, #ee5253); }
        .btn-return:hover .btn-icon { box-shadow: 0 4px 10px rgba(255, 107, 107, 0.3); }

        @media (max-width: 1200px) {
          .charts-grid { grid-template-columns: 1fr; }
          .bottom-grid-3-col { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default PharmacyDashboard;